import React, { useState, useRef, useEffect } from 'react'

const SAVED_COLORS_KEY = 'beu-email-saved-colors'

const loadSavedColors = () => {
  try {
    const stored = localStorage.getItem(SAVED_COLORS_KEY)
    return stored ? JSON.parse(stored) : []
  } catch { return [] }
}

const persistSavedColors = (colors) => {
  try {
    localStorage.setItem(SAVED_COLORS_KEY, JSON.stringify(colors))
  } catch {}
}

export const ColorPicker = ({ value, onChange, onClose, label, triggerRect }) => {
  const [hexInput, setHexInput] = useState(value || '#000000')
  const [activeTab, setActiveTab] = useState('preset')
  const [hue, setHue] = useState(0)
  const [saturation, setSaturation] = useState(100)
  const [brightness, setBrightness] = useState(100)
  const [savedColors, setSavedColors] = useState(loadSavedColors)
  const spectrumRef = useRef(null)
  const hueRef = useRef(null)
  const pickerRef = useRef(null)
  const [isDraggingSpectrum, setIsDraggingSpectrum] = useState(false)
  const [isDraggingHue, setIsDraggingHue] = useState(false)
  const [pickerStyle, setPickerStyle] = useState({})

  const presetColors = [
    '#000000', '#29282D', '#374151', '#6b7280', '#9ca3af', '#d1d5db', '#e5e7eb', '#f3f4f6', '#ffffff',
    '#007EA0', '#0891b2', '#06b6d4', '#22d3ee', '#67e8f9',
    '#059669', '#10b981', '#34d399', '#6ee7b7', '#a7f3d0',
    '#dc2626', '#ef4444', '#f87171', '#fca5a5', '#fecaca',
    '#d97706', '#f59e0b', '#fbbf24', '#fcd34d', '#fde68a',
    '#7c3aed', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe',
    '#db2777', '#ec4899', '#f472b6', '#f9a8d4', '#fbcfe8'
  ]

  // Position the picker so it stays within viewport
  useEffect(() => {
    if (!triggerRect) return
    const pickerHeight = 420
    const pickerWidth = 280
    const spaceBelow = window.innerHeight - triggerRect.bottom - 8
    const spaceAbove = triggerRect.top - 8
    const openUpward = spaceBelow < pickerHeight && spaceAbove > spaceBelow

    let left = triggerRect.left
    if (left + pickerWidth > window.innerWidth - 16) {
      left = window.innerWidth - pickerWidth - 16
    }
    if (left < 16) left = 16

    if (openUpward) {
      setPickerStyle({
        position: 'fixed',
        bottom: `${window.innerHeight - triggerRect.top + 4}px`,
        left: `${left}px`,
        width: `${pickerWidth}px`
      })
    } else {
      setPickerStyle({
        position: 'fixed',
        top: `${triggerRect.bottom + 4}px`,
        left: `${left}px`,
        width: `${pickerWidth}px`
      })
    }
  }, [triggerRect])

  // Convert HSB to Hex
  const hsbToHex = (h, s, b) => {
    s = s / 100
    b = b / 100
    const k = (n) => (n + h / 60) % 6
    const f = (n) => b * (1 - s * Math.max(0, Math.min(k(n), 4 - k(n), 1)))
    const toHex = (x) => Math.round(x * 255).toString(16).padStart(2, '0')
    return `#${toHex(f(5))}${toHex(f(3))}${toHex(f(1))}`
  }

  // Convert Hex to HSB
  const hexToHsb = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255
    const g = parseInt(hex.slice(3, 5), 16) / 255
    const b = parseInt(hex.slice(5, 7), 16) / 255
    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    const d = max - min
    let h = 0
    if (d !== 0) {
      if (max === r) h = ((g - b) / d + 6) % 6
      else if (max === g) h = (b - r) / d + 2
      else h = (r - g) / d + 4
      h *= 60
    }
    const s = max === 0 ? 0 : (d / max) * 100
    const v = max * 100
    return { h, s, v }
  }

  // Initialize HSB from value
  useEffect(() => {
    if (value && /^#[0-9A-Fa-f]{6}$/.test(value)) {
      const hsb = hexToHsb(value)
      setHue(hsb.h)
      setSaturation(hsb.s)
      setBrightness(hsb.v)
    }
  }, [])

  const updateColorFromHsb = (h, s, b) => {
    const hex = hsbToHex(h, s, b)
    setHexInput(hex)
    onChange(hex)
  }

  const handleSpectrumInteraction = (e) => {
    if (!spectrumRef.current) return
    const rect = spectrumRef.current.getBoundingClientRect()
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width))
    const y = Math.max(0, Math.min(e.clientY - rect.top, rect.height))
    const s = (x / rect.width) * 100
    const b = 100 - (y / rect.height) * 100
    setSaturation(s)
    setBrightness(b)
    updateColorFromHsb(hue, s, b)
  }

  const handleHueInteraction = (e) => {
    if (!hueRef.current) return
    const rect = hueRef.current.getBoundingClientRect()
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width))
    const h = (x / rect.width) * 360
    setHue(h)
    updateColorFromHsb(h, saturation, brightness)
  }

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDraggingSpectrum) handleSpectrumInteraction(e)
      if (isDraggingHue) handleHueInteraction(e)
    }
    const handleMouseUp = () => {
      setIsDraggingSpectrum(false)
      setIsDraggingHue(false)
    }
    if (isDraggingSpectrum || isDraggingHue) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDraggingSpectrum, isDraggingHue, hue, saturation, brightness])

  const handleHexChange = (e) => {
    let val = e.target.value
    if (!val.startsWith('#')) val = '#' + val
    setHexInput(val)
    if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
      const hsb = hexToHsb(val)
      setHue(hsb.h)
      setSaturation(hsb.s)
      setBrightness(hsb.v)
      onChange(val)
    }
  }

  const handleColorSelect = (color) => {
    setHexInput(color)
    const hsb = hexToHsb(color)
    setHue(hsb.h)
    setSaturation(hsb.s)
    setBrightness(hsb.v)
    onChange(color)
  }

  const saveColor = () => {
    if (/^#[0-9A-Fa-f]{6}$/.test(hexInput) && !savedColors.includes(hexInput)) {
      const updated = [...savedColors, hexInput].slice(-27) // max 27 (3 rows of 9)
      setSavedColors(updated)
      persistSavedColors(updated)
    }
  }

  return (
    <>
      {/* Backdrop - click to close */}
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 999 }}
      />
      <div
        ref={pickerRef}
        style={{
          ...pickerStyle,
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
          padding: '16px',
          zIndex: 1000
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a1a' }}>{label || 'Color'}</span>
          <button
            onClick={onClose}
            style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '4px', minWidth: '44px', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '12px', backgroundColor: '#f3f4f6', borderRadius: '8px', padding: '4px' }}>
          {['spectrum', 'preset', 'saved'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                flex: 1,
                padding: '6px 8px',
                border: 'none',
                borderRadius: '6px',
                fontSize: '11px',
                fontWeight: '500',
                cursor: 'pointer',
                backgroundColor: activeTab === tab ? 'white' : 'transparent',
                color: activeTab === tab ? '#1a1a1a' : '#6b7280',
                boxShadow: activeTab === tab ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.15s ease'
              }}
            >
              {tab === 'spectrum' ? 'Picker' : tab === 'preset' ? 'Presets' : 'Saved'}
            </button>
          ))}
        </div>

        {/* Spectrum Picker */}
        {activeTab === 'spectrum' && (
          <div style={{ marginBottom: '12px' }}>
            <div
              ref={spectrumRef}
              onMouseDown={(e) => { setIsDraggingSpectrum(true); handleSpectrumInteraction(e) }}
              style={{
                width: '100%',
                height: '140px',
                borderRadius: '8px',
                position: 'relative',
                cursor: 'crosshair',
                marginBottom: '12px',
                background: `linear-gradient(to bottom, transparent, black), linear-gradient(to right, white, hsl(${hue}, 100%, 50%))`
              }}
            >
              <div style={{
                position: 'absolute',
                left: `${saturation}%`,
                top: `${100 - brightness}%`,
                width: '14px',
                height: '14px',
                borderRadius: '50%',
                border: '2px solid white',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.2), 0 2px 4px rgba(0,0,0,0.2)',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none'
              }} />
            </div>

            <div
              ref={hueRef}
              onMouseDown={(e) => { setIsDraggingHue(true); handleHueInteraction(e) }}
              style={{
                width: '100%',
                height: '14px',
                borderRadius: '7px',
                position: 'relative',
                cursor: 'pointer',
                background: 'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)'
              }}
            >
              <div style={{
                position: 'absolute',
                left: `${(hue / 360) * 100}%`,
                top: '50%',
                width: '14px',
                height: '14px',
                borderRadius: '50%',
                border: '2px solid white',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.2), 0 2px 4px rgba(0,0,0,0.2)',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
                backgroundColor: `hsl(${hue}, 100%, 50%)`
              }} />
            </div>
          </div>
        )}

        {/* Preset Colors */}
        {activeTab === 'preset' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: '4px', marginBottom: '12px' }}>
            {presetColors.map((color, idx) => (
              <button
                key={idx}
                onClick={() => handleColorSelect(color)}
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '4px',
                  border: color === hexInput ? '2px solid #007EA0' : color === '#ffffff' ? '1px solid #e5e5e5' : 'none',
                  backgroundColor: color,
                  cursor: 'pointer',
                  boxShadow: color === hexInput ? '0 0 0 2px rgba(0,126,160,0.2)' : 'none'
                }}
              />
            ))}
          </div>
        )}

        {/* Saved Colors */}
        {activeTab === 'saved' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: '4px', marginBottom: '12px', minHeight: '56px' }}>
            {savedColors.map((color, idx) => (
              <button
                key={idx}
                onClick={() => handleColorSelect(color)}
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '4px',
                  border: color === hexInput ? '2px solid #007EA0' : color === '#ffffff' ? '1px solid #e5e5e5' : 'none',
                  backgroundColor: color,
                  cursor: 'pointer',
                  boxShadow: color === hexInput ? '0 0 0 2px rgba(0,126,160,0.2)' : 'none'
                }}
              />
            ))}
            {savedColors.length === 0 && (
              <div style={{ gridColumn: 'span 9', color: '#9ca3af', fontSize: '12px', textAlign: 'center', padding: '16px 0' }}>
                No saved colors yet
              </div>
            )}
          </div>
        )}

        {/* Hex Input */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            backgroundColor: hexInput,
            border: '1px solid #e5e5e5',
            flexShrink: 0
          }} />
          <div style={{ flex: 1 }}>
            <input
              type="text"
              value={hexInput}
              onChange={handleHexChange}
              maxLength={7}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e5e5e5',
                borderRadius: '8px',
                fontSize: '13px',
                fontFamily: 'monospace',
                boxSizing: 'border-box',
                textTransform: 'uppercase'
              }}
            />
          </div>
          <button
            onClick={saveColor}
            title="Save colour"
            style={{
              width: '36px',
              height: '36px',
              border: '1px solid #e5e5e5',
              borderRadius: '8px',
              backgroundColor: savedColors.includes(hexInput) ? '#f0fdfa' : 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={savedColors.includes(hexInput) ? '#007EA0' : '#6b7280'} strokeWidth="2">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
              <polyline points="17 21 17 13 7 13 7 21"/>
              <polyline points="7 3 7 8 15 8"/>
            </svg>
          </button>
        </div>
      </div>
    </>
  )
}
